import { PlanIndexes, websiteFlagKeys } from '@va/constants';
import { PlanType } from '@va/dashboard/api-client/pricing-plans.api';
import { updateWebsiteFrontendFlags } from '@va/dashboard/api-client/Settings';
import { getInstanceId, getUnlimitedVoucher } from '@va/dashboard/selectors/app';
import { getLastSeenFreeTrialModal, selectWebsiteMonthlyVisits } from '@va/dashboard/selectors/core';
import { useLocalizationContext } from '@va/localization';
import { addNumberSeparator } from '@va/util/helpers';
import dayjs from 'dayjs';
import { clamp } from 'lodash';
import { useCallback, useMemo } from 'react';
import { useSelector } from 'react-redux';
import { PricingPlanSuggestionModal } from '../PricingPlanSuggestionModal';

const daysLeftToShowModal = [15, 5, 2, 1, 0];

export const FreeTrialEndingModal = () => {
  const unlimitedVoucher = useSelector(getUnlimitedVoucher);
  const lastSeenModal = useSelector(getLastSeenFreeTrialModal);

  const daysLeft = useMemo(
    () =>
      unlimitedVoucher && unlimitedVoucher.voucherCode
        ? dayjs.unix(unlimitedVoucher.expiresAt).diff(dayjs(), 'day')
        : null,
    [unlimitedVoucher],
  );

  const isOpen = useMemo(
    () => daysLeftToShowModal.some((day) => day === daysLeft && lastSeenModal !== day),
    [daysLeft, lastSeenModal],
  );

  if (!isOpen || daysLeft === null) return null;

  return <FreeTrialEndingModalContent daysLeft={daysLeft} />;
};

const FreeTrialEndingModalContent = ({ daysLeft }: { daysLeft: number }) => {
  const websiteId = useSelector(getInstanceId);
  const visitsCount = useSelector(selectWebsiteMonthlyVisits);
  const { locale, translate } = useLocalizationContext();

  const daysPassed = useMemo(() => clamp(30 - daysLeft, 1, 30), [daysLeft]);

  const getRecommendedPlan = useCallback(
    (plans: Record<number, PlanType>) => {
      if (visitsCount === undefined || daysLeft === null) return null;

      const estimatedVisitsPerMonth = visitsCount * (30 / daysPassed);

      const recommendedPlan =
        Object.values(plans).find(
          (plan: PlanType) => plan.index !== PlanIndexes.Free && estimatedVisitsPerMonth <= plan.limit,
        ) ?? plans[PlanIndexes.ProPlus];

      return recommendedPlan;
    },
    [daysLeft, daysPassed, visitsCount],
  );

  const getPlanFeature = useCallback(
    (plan: PlanType) => {
      return translate('modals.freeTrialEnding.pricingPlanTile.viewsIncluded', { count: plan.limit });
    },
    [translate],
  );

  const onClose = useCallback(async () => {
    await updateWebsiteFrontendFlags(websiteId, { [websiteFlagKeys.LAST_SEEN_FREE_TRIAL_MODAL]: daysLeft });
  }, [daysLeft, websiteId]);

  const getTitle = () => {
    if (daysLeft <= 0) return translate('modals.freeTrialEnding.title.ended');
    if (daysLeft === 1) return translate('modals.freeTrialEnding.title.oneDay');

    return translate('modals.freeTrialEnding.title.daysCount', { count: daysLeft });
  };

  return (
    <PricingPlanSuggestionModal
      getRecommendedPlan={getRecommendedPlan}
      getPlanFeature={getPlanFeature}
      title={getTitle()}
      hint={translate('modals.freeTrialEnding.hintText')}
      onClose={onClose}
      isLoading={false}
      subtitle={
        <>
          {translate('modals.freeTrialEnding.pastDays', { count: daysPassed })}&nbsp;
          <span className='text-primary font-semibold'>
            {translate('modals.freeTrialEnding.pageViewsCount', {
              count: addNumberSeparator(visitsCount, locale),
            })}
          </span>
        </>
      }
    />
  );
};
